<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import { t, translate } from "./lib/i18n";

  import resultsIcon from "@iconify/icons-mdi/clipboard-text-outline";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import closeIcon from "@iconify/icons-mdi/close";
  import openInNewIcon from "@iconify/icons-mdi/open-in-new";
  import folderIcon from "@iconify/icons-mdi/folder-outline";
  import fileIcon from "@iconify/icons-mdi/file-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";
  import checkIcon from "@iconify/icons-mdi/check-circle-outline";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import magnifyIcon from "@iconify/icons-mdi/magnify";
  import chevronRightIcon from "@iconify/icons-mdi/chevron-right";
  import arrowUpIcon from "@iconify/icons-mdi/arrow-up";

  export let apiBase = "";
  export let apiKey = "";
  export let authMode = false; // IMPORTANT: passed from App.svelte

  const JSON_HEADERS = { Accept: "application/json" };

  // list state
  let rawItems = [];
  let items = [];
  let loading = false;
  let err = "";

  // paging (cursor-based)
  let cursor = 0;
  let nextCursor = -1;
  let cursorStack = [0];
  let limit = 25;
  let total = 0;

  // filters
  let q = "";
  let status = "all"; // all | finished | error

  // drawer state
  let drawerOpen = false;
  let selectedId = "";
  let selectedRow = null;

  // detail state
  let detail = null;
  let detailLoading = false;
  let detailErr = "";

  // storage listing state
  let storage = null;
  let storageLoading = false;
  let storageErr = "";

  // inflight aborts
  let inflightList = null;
  let inflightDetail = null;
  let inflightStorage = null;

  // pollers
  let listTimer = null;
  let drawerTimer = null;

  const LIST_POLL_MS = 2500;
  const DRAWER_POLL_MS = 2500;
  const POLLING_ENABLED = false;

  $: canPrev = cursorStack.length > 1;
  $: canNext = nextCursor !== -1;

  function keyTrim() {
    return String(apiKey || "").trim();
  }

  function canUseJSON() {
    // If auth is enabled, require key; if auth disabled, always allow.
    return !authMode || !!keyTrim();
  }

  function authHeaderMaybe() {
    const k = keyTrim();
    // Always safe: backend ignores if auth disabled.
    return k ? { Authorization: `Bearer ${k}` } : {};
  }

  function toQS(obj) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined || v === null) continue;
      const s = String(v).trim();
      if (!s) continue;
      p.set(k, s);
    }
    const out = p.toString();
    return out ? `?${out}` : "";
  }

  function safeStr(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  function fmtMs(ms) {
    const n = Number(ms || 0);
    if (!n) return "—";
    if (n < 1000) return `${n} ms`;
    return `${(n / 1000).toFixed(2)} s`;
  }

  function ts(v) {
    if (!v) return 0;
    const d = new Date(v);
    const n = d.getTime();
    return Number.isFinite(n) ? n : 0;
  }

  function fmtTime(v) {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  }

  function fmtBytes(n) {
    const v = Number(n || 0);
    if (!Number.isFinite(v) || v <= 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let x = v;
    let u = 0;
    while (x >= 1024 && u < units.length - 1) {
      x /= 1024;
      u++;
    }
    return `${x.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
  }

  function statusPill(s) {
    const v = String(s || "").toLowerCase();
    if (v === "finished" || v === "success")
      return "border-emerald-400/30 bg-emerald-500/10 text-emerald-100";
    if (v === "error" || v === "failed")
      return "border-rose-400/30 bg-rose-500/10 text-rose-100";
    return "border-white/10 bg-white/[0.03] text-zinc-200";
  }

  function statusIcon(s) {
    const v = String(s || "").toLowerCase();
    if (v === "finished" || v === "success") return checkIcon;
    if (v === "error" || v === "failed") return alertIcon;
    return clockIcon;
  }

  function normalizePath(p) {
    return (p || "").toString().replace(/^\/+|\/+$/g, "");
  }

  function filterItems(xs) {
    let out = xs || [];
    const needle = (q || "").trim().toLowerCase();
    if (needle) {
      out = out.filter((r) => {
        const hay = [
          jobIdOf(r),
          videoIdOf(r),
          tenantIdOf(r),
          inputPathOf(r),
          outputPathOf(r),
          r?.status,
          r?.stage
        ]
          .map((v) => (v ?? "").toString().toLowerCase())
          .filter(Boolean)
          .join(" ");
        return hay.includes(needle);
      });
    }
    if (status !== "all") {
      out = out.filter((r) => {
        const s = String(r?.status || r?.stage || "").toLowerCase();
        return s === status;
      });
    }
    return out;
  }

  $: items = filterItems(rawItems);

  function outputPathFromResult(r) {
    const k = r?.output?.key || r?.Output?.Key;
    if (k) return normalizePath(String(k));

    const od = r?.output_dir || r?.outputDir || r?.output_path || r?.outputPath;
    if (od) return normalizePath(String(od));

    return "";
  }

  function jobIdOf(r) {
    return String(r?.id || r?.job_id || r?.jobId || "");
  }

  function videoIdOf(r) {
    return safeStr(r?.video_id || r?.videoId);
  }

  function tenantIdOf(r) {
    return safeStr(r?.tenant_id || r?.tenantId);
  }

  function workerIdxOf(r) {
    const v = r?.worker_idx ?? r?.workerIndex ?? r?.worker_id ?? r?.workerId;
    return safeStr(v);
  }

  function inputPathOf(r) {
    const b = (r?.input?.bucket ?? r?.Input?.Bucket ?? "").toString();
    const k = (r?.input?.key ?? r?.Input?.Key ?? "").toString();
    const s = [b, k].filter(Boolean).join("/");
    return s || "—";
  }

  function outputPathOf(r) {
    const b = (r?.output?.bucket ?? r?.Output?.Bucket ?? "").toString();
    const k = (r?.output?.key ?? r?.Output?.Key ?? "").toString();
    const s = [b, k].filter(Boolean).join("/");
    return s || "—";
  }

  function shortKey(p) {
    const s = (p || "").toString().trim();
    if (!s) return "";
    const parts = s.split("/").filter(Boolean);
    if (parts.length <= 2) return s;
    return parts.slice(-2).join("/");
  }

  function sortEntries(arr) {
    const a = Array.isArray(arr) ? [...arr] : [];
    a.sort((x, y) => {
      if (!!x?.is_dir !== !!y?.is_dir) return x?.is_dir ? -1 : 1;
      const dt = ts(y?.last_modified) - ts(x?.last_modified);
      if (dt !== 0) return dt;
      return (x?.name || "").localeCompare(y?.name || "");
    });
    return a;
  }

  function parentPath(p) {
    const clean = normalizePath(p);
    if (!clean) return "";
    const parts = clean.split("/").filter(Boolean);
    parts.pop();
    return parts.join("/");
  }

  function openStoragePath(nextPath) {
    const p = normalizePath(nextPath);
    fetchStorage(p);
  }

  function stopListPolling() {
    if (!listTimer) return;
    clearInterval(listTimer);
    listTimer = null;
  }

  function startListPolling() {
    if (listTimer) return;
    if (!POLLING_ENABLED) return;
    listTimer = setInterval(() => {
      if (!canUseJSON()) return;
      if (!loading) fetchResults();
    }, LIST_POLL_MS);
  }

  function stopDrawerPolling() {
    if (!drawerTimer) return;
    clearInterval(drawerTimer);
    drawerTimer = null;
  }

  function startDrawerPolling() {
    if (drawerTimer) return;
    if (!POLLING_ENABLED) return;
    drawerTimer = setInterval(() => {
      if (!canUseJSON()) return;
      if (!drawerOpen || !selectedId) return;
      if (detailLoading || storageLoading) return;
      refreshDrawer();
    }, DRAWER_POLL_MS);
  }

  // Drawer open/close -> control drawer polling
  $: if (drawerOpen && selectedId) startDrawerPolling();
  $: if (!drawerOpen || !selectedId) stopDrawerPolling();

  // Auth gate: if auth is required but we have no key, stop pollers + cancel in-flight.
  $: if (!canUseJSON()) {
    stopListPolling();
    stopDrawerPolling();
    try { inflightList?.abort?.(); } catch {}
    try { inflightDetail?.abort?.(); } catch {}
    try { inflightStorage?.abort?.(); } catch {}
  } else {
    // ensure list polling runs when allowed
    //startListPolling();
  }

  // Abort ONLY when apiKey value actually changes (not every reactive tick)
  let lastKey = keyTrim();
  $: {
    const nowKey = keyTrim();
    if (nowKey !== lastKey) {
      lastKey = nowKey;
      try { inflightList?.abort?.(); } catch {}
      try { inflightDetail?.abort?.(); } catch {}
      try { inflightStorage?.abort?.(); } catch {}
      // Optional: refresh list immediately when key changes and auth allows
      if (canUseJSON()) fetchResults();
    }
  }

  async function fetchResults() {
    if (!canUseJSON()) return;

    loading = true;
    err = "";

    try {
      try { inflightList?.abort?.(); } catch {}
      inflightList = new AbortController();

      const qs = toQS({
        cursor,
        limit
      });

      const res = await fetch(`${apiBase}/results${qs}`, {
        cache: "no-store",
        signal: inflightList.signal,
        headers: { ...JSON_HEADERS, ...authHeaderMaybe() }
      });

      let body = null;
      try { body = await res.json(); } catch {}

      if (!res.ok) throw new Error(body?.error || `results failed: ${res.status}`);

      rawItems = body?.items || body?.data || body?.results || [];
      limit = Number(body?.limit || limit || 25);
      total = Number(body?.total ?? body?.count ?? body?.total_items ?? 0);
      nextCursor = Number.isFinite(Number(body?.next_cursor))
        ? Number(body?.next_cursor)
        : -1;
    } catch (e) {
      if (e?.name === "AbortError") return;
      rawItems = [];
      total = 0;
      nextCursor = -1;
      err = e?.message || String(e);
    } finally {
      loading = false;
    }
  }

  async function fetchDetail(id) {
    if (!canUseJSON()) return;

    detail = null;
    detailErr = "";
    detailLoading = true;

    try {
      try { inflightDetail?.abort?.(); } catch {}
      inflightDetail = new AbortController();

      const res = await fetch(`${apiBase}/results/${encodeURIComponent(id)}`, {
        cache: "no-store",
        signal: inflightDetail.signal,
        headers: { ...JSON_HEADERS, ...authHeaderMaybe() }
      });

      let body = null;
      try { body = await res.json(); } catch {}

      if (!res.ok) throw new Error(body?.error || `result failed: ${res.status}`);
      detail = body;
    } catch (e) {
      if (e?.name === "AbortError") return;
      detail = null;
      detailErr = e?.message || String(e);
    } finally {
      detailLoading = false;
    }
  }

  async function fetchStorage(outPath) {
    if (!canUseJSON()) return;

    const p = normalizePath(outPath);

    storage = null;
    storageErr = "";
    storageLoading = true;

    if (!p) {
      storageLoading = false;
      storageErr = translate('results.no_output_path');
      return;
    }

    const encoded = p
      .split("/")
      .filter(Boolean)
      .map(encodeURIComponent)
      .join("/");

    const url = `${apiBase}/storage/${encoded}`;

    try {
      try { inflightStorage?.abort?.(); } catch {}
      inflightStorage = new AbortController();

      const res = await fetch(url, {
        cache: "no-store",
        signal: inflightStorage.signal,
        headers: { ...JSON_HEADERS, ...authHeaderMaybe() }
      });

      let body = null;
      try { body = await res.json(); } catch {}

      if (!res.ok) throw new Error(body?.error || `storage failed: ${res.status}`);
      storage = body;
    } catch (e) {
      if (e?.name === "AbortError") return;
      storage = null;
      storageErr = e?.message || String(e);
    } finally {
      storageLoading = false;
    }
  }

  async function openRow(row) {
    if (!canUseJSON()) return;

    const id = jobIdOf(row);
    if (!id) return;

    drawerOpen = true;
    selectedId = id;
    selectedRow = row || null;

    detail = null;
    detailErr = "";
    storage = null;
    storageErr = "";

    await fetchDetail(id);

    const outPath = outputPathFromResult(detail || row);
    await fetchStorage(outPath);
  }

  async function refreshDrawer() {
    if (!canUseJSON()) return;
    if (!drawerOpen || !selectedId) return;

    await fetchDetail(selectedId);
    const outPath = outputPathFromResult(detail || selectedRow);
    await fetchStorage(outPath);
  }

  function closeDrawer() {
    drawerOpen = false;
    selectedId = "";
    selectedRow = null;
    detail = null;
    detailErr = "";
    storage = null;
    storageErr = "";
  }

  function applyFilters() {
    cursor = 0;
    nextCursor = -1;
    cursorStack = [0];
    fetchResults();
  }

  function prev() {
    if (!canPrev) return;
    cursorStack.pop();
    cursor = cursorStack[cursorStack.length - 1] || 0;
    fetchResults();
  }

  function next() {
    if (!canNext) return;
    cursor = nextCursor;
    cursorStack = [...cursorStack, nextCursor];
    fetchResults();
  }

  onMount(() => {
    // Start once; the reactive auth gate will keep polling running only when allowed.
    fetchResults();
    startListPolling();
  });

  onDestroy(() => {
    stopListPolling();
    stopDrawerPolling();
    try { inflightList?.abort?.(); } catch {}
    try { inflightDetail?.abort?.(); } catch {}
    try { inflightStorage?.abort?.(); } catch {}
  });

  // UI tokens
  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";

  const btn =
    "inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-200 hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed";

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-white/20";

  const selectCls =
    "rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-white/20";

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";

  const rowBase =
    "w-full text-left rounded-xl border transition-colors p-4 relative overflow-hidden";

  const rowIdle =
    "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]";

  const rowSelected =
    "border-[rgba(147,90,255,0.70)] " +
    "bg-gradient-to-r from-[rgba(147,90,255,0.18)] via-[rgba(147,90,255,0.08)] to-transparent " +
    "ring-1 ring-[rgba(147,90,255,0.55)] " +
    "before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] " +
    "before:bg-[rgb(147,90,255)] before:rounded-none " +
    "before:shadow-[0_0_22px_rgba(147,90,255,0.95)]";

  const card =
    "rounded-xl border border-white/5 bg-white/[0.02]";

  const klabel =
    "text-[11px] uppercase tracking-wide text-zinc-500";

  const kval =
    "text-sm text-zinc-200";

  const subline =
    "text-xs text-zinc-500";

  const pill =
    "inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold";

  const fileRowBtn =
    "w-full text-left flex items-center justify-between gap-3 px-3 py-2 " +
    "hover:bg-white/[0.04] transition-colors";
</script>


<main class="w-full px-4 py-6">
  <div class="flex gap-4">
    <!-- MAIN LIST -->
    <section class={`ui-card p-5 sm:p-6 min-w-0 flex-1 ${drawerOpen ? "lg:mr-[520px]" : ""}`}>
      <!-- Header -->
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <span class="inline-flex h-9 w-9 items-center justify-center border border-amber-400/40 bg-amber-500/50 text-amber-50">
            <Icon icon={resultsIcon} width="18" height="18" />
          </span>

          <div>
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{$t('results.title')}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">
              {$t('results.subtitle')}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <div class={headerChip} title={$t('common.base')}>
            <span class="text-zinc-400">{$t('common.base')}</span>
            <span class="font-medium text-zinc-200">{apiBase || $t('common.same_origin')}</span>
          </div>

          <div class={headerChip} title={$t('common.total')}>
            <span class="text-zinc-400">{$t('common.total')}</span>
            <span class="font-medium text-zinc-200">{total || 0}</span>
          </div>

          <button class={btn} on:click={fetchResults} disabled={loading} title={$t('common.refresh')}>
            <Icon icon={refreshIcon} width="18" height="18" />
          </button>
        </div>
      </div>

      <!-- Controls -->
      <div class="mt-6 grid gap-3 lg:grid-cols-[1fr_auto_auto_auto]">
        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500 flex items-center gap-2">
            <Icon icon={magnifyIcon} width="14" height="14" />
            <span>{$t('results.search_hint')}</span>
          </div>
          <input
            class={inputCls}
            placeholder={$t('common.search')}
            bind:value={q}
            on:keydown={(e) => e.key === "Enter" && applyFilters()}
          />
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('common.status')}</div>
          <select class={selectCls} bind:value={status} on:change={applyFilters}>
            <option value="all">{$t('common.all')}</option>
            <option value="finished">{$t('results.finished')}</option>
            <option value="error">{$t('common.error')}</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('common.page_size')}</div>
          <select class={selectCls} bind:value={limit} on:change={applyFilters}>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('common.cursor')}</div>
          <div class="flex items-center gap-2">
            <button class={btn} on:click={prev} disabled={loading || !canPrev}>{$t('common.prev')}</button>
            <div class="text-xs text-zinc-400">
              {$t('common.offset')} <span class="text-zinc-200 font-medium">{cursor}</span>
            </div>
            <button class={btn} on:click={next} disabled={loading || !canNext}>{$t('common.next')}</button>
          </div>
        </div>
      </div>

      {#if err}
        <div class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
          {err}
        </div>
      {/if}

      <!-- List -->
      <div class="mt-6">
        {#if loading}
          <div class="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('common.loading')}
          </div>
        {:else if !items?.length}
          <div class="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('results.no_results')}
          </div>
        {:else}
          <div class="grid gap-3">
            {#each items as r, i (r.id || r.job_id || i)}
              {@const rid = String(r.id || r.job_id || "")}

              <button
                type="button"
                class={`${rowBase} ${selectedId === rid ? rowSelected : rowIdle}`}
                on:click={() => openRow(r)}
              >
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div class="min-w-0 space-y-1">
                    <div class="font-mono text-sm text-zinc-100 truncate" title={jobIdOf(r)}>
                      {jobIdOf(r)}
                    </div>

                    <div class="flex items-center gap-2 min-w-0 text-xs text-zinc-400">
                      <span class="shrink-0">{$t('results.input')}</span>
                      <span class="font-mono text-zinc-300 truncate" title={safeStr(r.input?.key)}>
                        {shortKey(r.input?.key) || "—"}
                      </span>
                    </div>

                <div class="flex items-center gap-2 min-w-0 text-xs text-zinc-400">
                  <span class="shrink-0">{$t('results.video_id')}</span>
                  <span class="font-mono text-zinc-300 truncate" title={videoIdOf(r)}>
                    {videoIdOf(r)}
                  </span>
                </div>
                  </div>

                  <div class="shrink-0 text-right">
                    <div class="text-xs text-zinc-500">{$t('results.tenant')}</div>
                    <div class="text-xs text-zinc-300 truncate max-w-[220px]" title={tenantIdOf(r)}>
                      {tenantIdOf(r)}
                    </div>
                  </div>
                </div>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </section>

    <!-- RIGHT DRAWER -->
    {#if drawerOpen}
      <aside
        class="hidden lg:block fixed right-0 top-[88px] bottom-0 w-[520px]
               border-l border-white/10 bg-black/40 backdrop-blur-xl"
      >
        <div class="h-full overflow-auto p-6">
          <div class={`${card} p-5`}>
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                  {$t('results.result')}
                </div>
                <div class="mt-1 text-xs text-zinc-500 truncate" title={selectedId}>
                  {selectedId}
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <a
                  class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"
                  href={`${apiBase}/results/${encodeURIComponent(selectedId)}`}
                  target="_blank"
                  rel="noreferrer"
                  title={$t('results.open_result_json')}
                >
                  <Icon icon={openInNewIcon} width="16" height="16" />
                  <span>{$t('common.json')}</span>
                </a>

                <button
                  class={btn}
                  on:click={refreshDrawer}
                  disabled={detailLoading || storageLoading}
                  title={$t('results.refresh_detail')}
                >
                  <Icon icon={refreshIcon} width="18" height="18" />
                </button>

                <button class={btn} on:click={closeDrawer} title={$t('common.close')}>
                  <Icon icon={closeIcon} width="18" height="18" />
                </button>
              </div>
            </div>

            <div class="mt-4 border-t border-white/10 pt-4">
              <div class={`${card} p-4`}>
                <div class="flex items-center justify-between">
                  <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                    {$t('results.job_details')}
                  </div>
                  {#if detail?.status || detail?.stage}
                    <span class={`${pill} ${statusPill(detail?.status || detail?.stage)}`}>
                      <Icon icon={statusIcon(detail?.status || detail?.stage)} width="14" height="14" />
                      <span>{safeStr(detail?.status || detail?.stage)}</span>
                    </span>
                  {/if}
                </div>

                {#if detailLoading}
                  <div class="mt-3 rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-zinc-400">
                    {$t('common.loading')}
                  </div>
                {:else if detailErr}
                  <div class="mt-3 rounded-xl border border-rose-400/35 bg-rose-400/10 p-3 text-sm text-rose-100">
                    {detailErr}
                  </div>
                {:else if detail}
                  <div class="mt-4 grid gap-4">
                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.status')}</div>
                        <div class={kval}>{safeStr(detail.status || detail.stage)}</div>
                      </div>

                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.worker')}</div>
                        <div class={kval}>{workerIdxOf(detail)}</div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.video_id')}</div>
                        <div class={mono} title={videoIdOf(detail)}>{videoIdOf(detail)}</div>
                      </div>

                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.tenant')}</div>
                        <div class={mono} title={tenantIdOf(detail)}>{tenantIdOf(detail)}</div>
                      </div>
                    </div>

                    <div class="grid gap-3">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.input')}</div>
                        <div class={mono} title={inputPathOf(detail)}>{inputPathOf(detail)}</div>
                      </div>

                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.output')}</div>
                        <div class={mono} title={outputPathOf(detail)}>{outputPathOf(detail)}</div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.source_duration')}</div>
                        <div class={subline}>{safeStr(detail.duration || "—")}s</div>
                      </div>
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.source_resolution')}</div>
                        <div class={subline}>
                          {safeStr(detail.source_width || "—")}x{safeStr(detail.source_height || "—")}
                        </div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.strategy')}</div>
                        <div class={subline}>{safeStr(detail.strategy || "—")}</div>
                      </div>
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.chunks_used')}</div>
                        <div class={subline}>{safeStr(detail.chunks_used ?? "—")}</div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.master_playlist')}</div>
                        <div class={mono} title={safeStr(detail.master_playlist)}>
                          {safeStr(detail.master_playlist)}
                        </div>
                      </div>
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.manifest')}</div>
                        <div class={mono} title={safeStr(detail.manifest_path)}>
                          {safeStr(detail.manifest_path)}
                        </div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.thumbnails')}</div>
                        <div class={subline}>
                          {safeStr(detail.thumb_count ?? "—")} @ {safeStr(detail.thumb_interval ?? "—")}s
                        </div>
                      </div>
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.worker')}</div>
                        <div class={subline}>{safeStr(detail.worker_id || detail.workerId || "—")}</div>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.ended')}</div>
                        <div class={subline}>{safeStr(detail.ended_at || detail.endedAt)}</div>
                      </div>

                      <div class="min-w-0">
                        <div class={klabel}>{$t('results.duration')}</div>
                        <div class={subline}>{fmtMs(detail.duration_ms ?? detail.durationMs)}</div>
                      </div>
                    </div>

                    {#if detail.profile_outputs}
                      <div class="mt-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                        <div class="text-xs text-zinc-400">{$t('results.profile_outputs')}</div>
                        <div class="mt-2 space-y-2">
                          {#each Object.entries(detail.profile_outputs) as [pid, po]}
                            <div class="grid grid-cols-1 gap-1 sm:grid-cols-[140px_1fr_120px] sm:items-center text-sm">
                              <div class="font-mono text-zinc-200">{pid}</div>
                              <div class="text-zinc-400">
                                {po.format} · {po.width}x{po.height} · {fmtBytes(po.size_bytes)}
                              </div>
                              <div class="text-right text-zinc-300">{safeStr(po.status)}</div>
                            </div>
                          {/each}
                        </div>
                      </div>
                    {/if}
                  </div>
                {:else}
                  <div class="mt-3 text-sm text-zinc-400">{$t('results.no_detail')}</div>
                {/if}
              </div>

              <div class="mt-5">
                <div class="flex items-center justify-between">
                  <div class="text-xs font-medium uppercase tracking-wide text-zinc-400 flex items-center gap-2">
                    <Icon icon={folderIcon} width="16" height="16" />
                    <span>{$t('results.output_files')}</span>
                  </div>

                  <div class="flex items-center gap-2">
                    <button
                      class={btn}
                      on:click={() => openStoragePath(parentPath(outputPathFromResult(detail || selectedRow)))}
                      disabled={!outputPathFromResult(detail || selectedRow) || storageLoading}
                      title={$t('results.up_one_level')}
                    >
                      <Icon icon={arrowUpIcon} width="18" height="18" />
                    </button>

                    <a
                      class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"
                      href={`${apiBase}/storage/${
                        outputPathFromResult(detail || selectedRow)
                          .split("/")
                          .filter(Boolean)
                          .map(encodeURIComponent)
                          .join("/")
                      }`}
                      target="_blank"
                      rel="noreferrer"
                      title={$t('results.open_storage_json')}
                    >
                      <Icon icon={openInNewIcon} width="16" height="16" />
                      <span>{$t('common.json')}</span>
                    </a>

                    <button
                      class={btn}
                      on:click={() => fetchStorage(outputPathFromResult(detail || selectedRow))}
                      disabled={storageLoading}
                      title={$t('results.refresh_files')}
                    >
                      <Icon icon={refreshIcon} width="18" height="18" />
                    </button>
                  </div>
                </div>

                <div class="mt-1 text-xs text-zinc-500">
                  {#if outputPathFromResult(detail || selectedRow)}
                    <span class="font-mono text-zinc-400">
                      {outputPathFromResult(detail || selectedRow)}
                    </span>
                  {:else}
                    <span>—</span>
                  {/if}
                </div>

                {#if storageLoading}
                  <div class="mt-3 rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-zinc-400">
                    {$t('common.loading')}
                  </div>
                {:else if storageErr}
                  <div class="mt-3 rounded-xl border border-yellow-400/35 bg-yellow-400/10 p-3 text-sm text-yellow-100">
                    {storageErr}
                  </div>
                {:else if storage}
                  {@const entries = sortEntries(storage?.entries)}
                  <div class="mt-3 rounded-xl border border-white/10 overflow-hidden">
                    <div class="max-h-[52vh] overflow-auto">
                      {#if !entries.length}
                        <div class="p-4 text-sm text-zinc-400">{$t('common.no_items')}</div>
                      {:else}
                        <div class="divide-y divide-white/10">
                          {#each entries as e (e.key)}
                            {#if e.is_dir}
                              <button
                                type="button"
                                class={fileRowBtn}
                                on:click={() => fetchStorage(normalizePath(`${outputPathFromResult(detail || selectedRow)}/${e.name}`))}
                                title={e.key}
                              >
                                <div class="min-w-0 flex items-center gap-2">
                                  <Icon icon={folderIcon} width="16" height="16" class="shrink-0 text-zinc-300" />
                                  <div class="min-w-0">
                                    <div class="text-sm font-semibold text-zinc-100 truncate">
                                      {e.rel_key || `${e.name}/`}
                                    </div>
                                    <div class="text-xs text-zinc-500">
                                      {e.last_modified ? fmtTime(e.last_modified) : "—"}
                                    </div>
                                  </div>
                                </div>
                                <Icon icon={chevronRightIcon} width="18" height="18" class="text-zinc-600" />
                              </button>
                            {:else}
                              <div class={fileRowBtn} title={e.key}>
                                <div class="min-w-0 flex items-center gap-2">
                                  <Icon icon={fileIcon} width="16" height="16" class="shrink-0 text-zinc-400" />
                                  <div class="min-w-0">
                                    <div class="text-sm text-zinc-200 truncate">
                                      {e.rel_key || e.name}
                                    </div>
                                    <div class="text-xs text-zinc-500">
                                      {fmtBytes(e.size)} • {e.last_modified ? fmtTime(e.last_modified) : "—"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            {/if}
                          {/each}
                        </div>
                      {/if}
                    </div>
                  </div>
                {:else}
                  <div class="mt-3 rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-zinc-400">
                    {$t('results.no_storage')}
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Mobile drawer -->
      <div class="lg:hidden w-full mt-4">
        <div class="ui-card p-5">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="text-sm font-semibold text-zinc-100">{$t('results.result')}</div>
              <div class="mt-1 font-mono text-xs text-zinc-300 truncate" title={selectedId}>
                {selectedId}
              </div>
            </div>

            <button class={btn} on:click={closeDrawer} title={$t('common.close')}>
              <Icon icon={closeIcon} width="18" height="18" />
            </button>
          </div>

          <div class="mt-4">
            {#if detailLoading}
              <div class="rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-zinc-400">
                {$t('common.loading')}
              </div>
            {:else if detailErr}
              <div class="rounded-xl border border-rose-400/35 bg-rose-400/10 p-3 text-sm text-rose-100">
                {detailErr}
              </div>
            {:else if detail}
              <div class="rounded-xl border border-white/10 bg-black/20 p-4">
                <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">{$t('results.job_details')}</div>
                <div class="mt-3 grid gap-2 text-sm">
                  <div class="flex justify-between gap-3">
                    <div class="text-zinc-500">{$t('results.status')}</div>
                    <div class="text-zinc-200">{safeStr(detail.status || detail.stage)}</div>
                  </div>
                  <div class="flex justify-between gap-3">
                    <div class="text-zinc-500">{$t('results.worker')}</div>
                    <div class="text-zinc-200">{workerIdxOf(detail)}</div>
                  </div>
                  <div class="flex justify-between gap-3">
                    <div class="text-zinc-500">{$t('results.video_id')}</div>
                    <div class="font-mono text-xs text-zinc-200">{videoIdOf(detail)}</div>
                  </div>
                  <div class="flex justify-between gap-3">
                    <div class="text-zinc-500">{$t('results.tenant')}</div>
                    <div class="font-mono text-xs text-zinc-200">{tenantIdOf(detail)}</div>
                  </div>
                </div>
              </div>
            {/if}

            <div class="mt-4">
              {#if storageLoading}
                <div class="rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-zinc-400">
                  {$t('common.loading')}
                </div>
              {:else if storageErr}
                <div class="rounded-xl border border-yellow-400/35 bg-yellow-400/10 p-3 text-sm text-yellow-100">
                  {storageErr}
                </div>
              {:else if storage}
                {@const entries = sortEntries(storage?.entries)}
                <div class="rounded-xl border border-white/10 overflow-hidden">
                  <div class="divide-y divide-white/10">
                    {#each entries as e (e.key)}
                      <div class="px-3 py-2">
                        <div class="text-sm text-zinc-200">{e.rel_key || e.name}</div>
                        <div class="text-xs text-zinc-500">
                          {e.is_dir ? "dir" : fmtBytes(e.size)} • {e.last_modified ? fmtTime(e.last_modified) : "—"}
                        </div>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
</main>
